import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { formatNumber, formatCurrency, formatQuantity } from '../../../utils/formatters';
import { API_DOCS } from './StoricoPrezziArticoloPage.api';

// ── Tipi ──────────────────────────────────────────────────────────────────────
// I campi Decimal di Prisma arrivano serializzati come stringa: tipizzati larghi
// e normalizzati con toNum().
type Dec = string | number | null;

interface UnitaMisuraLite { id: number; simbolo: string; nome: string; }

interface FornitoreLite {
  id: number;
  codice: string | null;
  ragioneSociale: string | null;
  nome: string | null;
}

interface ArticoloStorico {
  id: number;
  societaId: number;
  codice: string;
  descrizioneBase: string;
  tipologia: string;
  prezzoStandard: Dec;
  prezzoListinoVendita: Dec;
  convertitore: Dec;
  updatedAt: string;
  umVendita: UnitaMisuraLite | null;
  umAcquisto: UnitaMisuraLite | null;
  fornitore: FornitoreLite | null;
  ultimoImport: { id: number; etichetta: string; importatoAt: string; stato: string } | null;
}

type StatoRiga = 'DA_IMPORTARE' | 'IMPORTATO' | 'AGGIORNATO' | 'ERRORE';

interface RigaStorico {
  id: number;
  importListinoId: number;
  codiceArticolo: string;
  descrizioneBase: string;
  umVendita: string;
  umAcquisto: string | null;
  convertitore: Dec;
  prezzoStandard: Dec;
  qtaMinOrdinabile: Dec;
  qtaMinAcquisto: Dec;
  qtaPerPallet: Dec;
  stato: StatoRiga;
  erroreDettaglio: string | null;
  importListino: {
    id: number;
    etichetta: string;
    note: string | null;
    importatoAt: string;
    stato: string;
    utenteName: string;
    strategiaAggiornamento: 'SOLO_PREZZO' | 'PREZZO_E_ANAGRAFICA';
    fornitore: FornitoreLite | null;
  } | null;
}

interface Riepilogo {
  nRighe: number;
  nRigheApplicate: number;
  nImport: number;
  nFornitori: number;
  primoImportAt: string | null;
  ultimoImportAt: string | null;
  prezzoMin: number | null;
  prezzoMax: number | null;
}

interface RispostaStorico {
  success: boolean;
  data: { articolo: ArticoloStorico; righe: RigaStorico[]; riepilogo: Riepilogo };
}

// ── Helper ────────────────────────────────────────────────────────────────────

/** Normalizza un Decimal Prisma (stringa o numero) in number, o null. */
function toNum(v: Dec | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function formatDataOra(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function nomeFornitore(f: FornitoreLite | null | undefined): string {
  if (!f) return '—';
  return f.ragioneSociale || f.nome || `#${f.id}`;
}

/** Una riga ha davvero aggiornato il prezzo in anagrafica solo in questi stati. */
function isApplicata(r: RigaStorico): boolean {
  return r.stato === 'IMPORTATO' || r.stato === 'AGGIORNATO';
}

const STATO_RIGA_LABEL: Record<StatoRiga, string> = {
  DA_IMPORTARE: 'Da importare',
  IMPORTATO:    'Importato',
  AGGIORNATO:   'Aggiornato',
  ERRORE:       'Errore',
};

const STATO_RIGA_BADGE: Record<StatoRiga, string> = {
  DA_IMPORTARE: 'bop-badge-neutral',
  IMPORTATO:    'bop-badge-success',
  AGGIORNATO:   'bop-badge-info',
  ERRORE:       'bop-badge-danger',
};

// ── Pagina ────────────────────────────────────────────────────────────────────

const StoricoPrezziArticoloPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [articolo, setArticolo]   = useState<ArticoloStorico | null>(null);
  const [righe, setRighe]         = useState<RigaStorico[]>([]);
  const [riepilogo, setRiepilogo] = useState<Riepilogo | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [soloApplicate, setSoloApplicate] = useState(false);

  useEffect(() => {
    let annullato = false;
    const carica = async () => {
      setLoading(true);
      setError(null);
      try {
        // @api GET /api/anagrafiche/articoli/:id/storico-prezzi -> {"success":true,"data":{"articolo":{"id":"number","codice":"string","descrizioneBase":"string","prezzoStandard":"string|null","prezzoListinoVendita":"string|null","convertitore":"string","umVendita":"object|null","umAcquisto":"object|null","fornitore":"object|null","ultimoImport":"object|null","updatedAt":"string"},"righe":[{"id":"number","importListinoId":"number","prezzoStandard":"string","stato":"string","importListino":"object"}],"riepilogo":{"nRighe":"number","nRigheApplicate":"number","nImport":"number","nFornitori":"number","primoImportAt":"string|null","ultimoImportAt":"string|null","prezzoMin":"number|null","prezzoMax":"number|null"}}}
        const res = await apiFetch<RispostaStorico>(`/api/anagrafiche/articoli/${id}/storico-prezzi`);
        if (annullato) return;
        setArticolo(res.data.articolo);
        setRighe(res.data.righe || []);
        setRiepilogo(res.data.riepilogo);
      } catch (err) {
        if (annullato) return;
        setError(err instanceof Error ? err.message : 'Errore nel caricamento dello storico prezzi');
      } finally {
        if (!annullato) setLoading(false);
      }
    };
    carica();
    return () => { annullato = true; };
  }, [id]);

  // Variazione rispetto all'import applicato precedente. Calcolata solo sulla
  // sequenza delle righe applicate: le righe non applicate non hanno mai
  // toccato il prezzo in anagrafica, quindi non definiscono un "prima".
  const deltaPerRiga = useMemo(() => {
    const mappa = new Map<number, number | null>();
    const applicate = righe.filter(isApplicata); // già ordinate dal più recente
    applicate.forEach((r, idx) => {
      const corrente   = toNum(r.prezzoStandard);
      const precedente = toNum(applicate[idx + 1]?.prezzoStandard);
      mappa.set(
        r.id,
        corrente != null && precedente != null && precedente !== 0
          ? ((corrente - precedente) / precedente) * 100
          : null,
      );
    });
    return mappa;
  }, [righe]);

  const righeVisibili = useMemo(
    () => (soloApplicate ? righe.filter(isApplicata) : righe),
    [righe, soloApplicate],
  );

  const convertitoreArticolo = toNum(articolo?.convertitore) ?? 1;
  const simboloAcquisto = articolo?.umAcquisto?.simbolo || 'u.m. acq.';
  const simboloVendita  = articolo?.umVendita?.simbolo  || 'u.m. vend.';

  if (loading) {
    return (
      <Layout>
        <div className="articolo-prezzi">
          <p className="bop-text-muted">Caricamento storico prezzi…</p>
        </div>
      </Layout>
    );
  }

  if (error || !articolo) {
    return (
      <Layout>
        <div className="articolo-prezzi">
          <BopBackLink to="/anagrafiche/articoli" label="Torna agli articoli" />
          <div className="bop-card">
            <p className="bop-text-danger">{error || 'Articolo non trovato'}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="articolo-prezzi">
        <BopBackLink to={`/anagrafiche/articoli/${articolo.id}`} label="Torna alla scheda articolo" />

        <div className="bop-page-header">
          <h1 className="bop-page-title">
            Storico prezzi di listino — {articolo.codice}
          </h1>
        </div>

        {/* ── Prezzo attuale in anagrafica ───────────────────────────────── */}
        <div className="bop-card">
          <div className="bop-card__header">
            <span className="bop-card__title">Prezzo attuale in anagrafica</span>
            <Link
              to={`/anagrafiche/articoli/${articolo.id}`}
              className="articolo-prezzi__link"
            >
              Apri la scheda articolo
            </Link>
          </div>
          <div className="bop-card__body">
            <p className="articolo-prezzi__descrizione">{articolo.descrizioneBase}</p>

            <div className="articolo-prezzi__grid">
              <div className="articolo-prezzi__voce articolo-prezzi__voce--forte">
                <span className="bop-label">Prezzo standard (acquisto)</span>
                <span className="articolo-prezzi__valore">
                  € {formatCurrency(toNum(articolo.prezzoStandard))} / {simboloAcquisto}
                </span>
              </div>
              <div className="articolo-prezzi__voce">
                <span className="bop-label">Convertitore</span>
                <span className="articolo-prezzi__valore">
                  1 {simboloVendita} = {formatNumber(convertitoreArticolo)} {simboloAcquisto}
                </span>
              </div>
              <div className="articolo-prezzi__voce articolo-prezzi__voce--forte">
                <span className="bop-label">Prezzo listino vendita</span>
                <span className="articolo-prezzi__valore">
                  € {formatCurrency(toNum(articolo.prezzoListinoVendita))} / {simboloVendita}
                </span>
              </div>
              <div className="articolo-prezzi__voce">
                <span className="bop-label">Fornitore in anagrafica</span>
                <span className="articolo-prezzi__valore">{nomeFornitore(articolo.fornitore)}</span>
              </div>
              <div className="articolo-prezzi__voce">
                <span className="bop-label">Ultimo import applicato</span>
                <span className="articolo-prezzi__valore">
                  {articolo.ultimoImport
                    ? (
                      <Link
                        to={`/anagrafiche/articoli/listini/${articolo.ultimoImport.id}`}
                        className="articolo-prezzi__link"
                      >
                        {articolo.ultimoImport.etichetta}
                      </Link>
                    )
                    : '—'}
                </span>
              </div>
              <div className="articolo-prezzi__voce">
                <span className="bop-label">Anagrafica aggiornata il</span>
                <span className="articolo-prezzi__valore">{formatDataOra(articolo.updatedAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Riepilogo import ───────────────────────────────────────────── */}
        {riepilogo && (
          <div className="bop-card">
            <div className="bop-card__header">
              <span className="bop-card__title">Riepilogo import</span>
            </div>
            <div className="bop-card__body">
              <div className="articolo-prezzi__kpi-row">
                <div className="articolo-prezzi__kpi">
                  <span className="articolo-prezzi__kpi-valore">{riepilogo.nImport}</span>
                  <span className="articolo-prezzi__kpi-label">
                    {riepilogo.nImport === 1 ? 'Import trovato' : 'Import trovati'}
                  </span>
                </div>
                <div className="articolo-prezzi__kpi">
                  <span className="articolo-prezzi__kpi-valore">{riepilogo.nRigheApplicate}</span>
                  <span className="articolo-prezzi__kpi-label">
                    Righe applicate su {riepilogo.nRighe}
                  </span>
                </div>
                <div className="articolo-prezzi__kpi">
                  <span className="articolo-prezzi__kpi-valore">{riepilogo.nFornitori}</span>
                  <span className="articolo-prezzi__kpi-label">
                    {riepilogo.nFornitori === 1 ? 'Fornitore' : 'Fornitori'}
                  </span>
                </div>
                <div className="articolo-prezzi__kpi">
                  <span className="articolo-prezzi__kpi-valore">
                    € {formatCurrency(riepilogo.prezzoMin)}
                  </span>
                  <span className="articolo-prezzi__kpi-label">Prezzo minimo applicato</span>
                </div>
                <div className="articolo-prezzi__kpi">
                  <span className="articolo-prezzi__kpi-valore">
                    € {formatCurrency(riepilogo.prezzoMax)}
                  </span>
                  <span className="articolo-prezzi__kpi-label">Prezzo massimo applicato</span>
                </div>
              </div>
              <p className="articolo-prezzi__periodo bop-text-muted">
                Primo import: {formatDataOra(riepilogo.primoImportAt)} · Ultimo import:{' '}
                {formatDataOra(riepilogo.ultimoImportAt)}
              </p>
            </div>
          </div>
        )}

        {/* ── Tabella storico ───────────────────────────────────────────── */}
        <div className="bop-card">
          <div className="bop-card__header">
            <span className="bop-card__title">
              Righe di listino importate ({righeVisibili.length})
            </span>
            <label className="articolo-prezzi__toggle">
              <input
                type="checkbox"
                checked={soloApplicate}
                onChange={e => setSoloApplicate(e.target.checked)}
              />
              Solo righe applicate
            </label>
          </div>
          <div className="bop-card__body">
            {righe.length === 0 ? (
              <div className="bop-empty-state">
                Nessuna riga di listino trovata per il codice <strong>{articolo.codice}</strong>.
                Il prezzo in anagrafica è stato inserito a mano oppure gli import
                usavano un codice articolo diverso.
              </div>
            ) : (
              <div className="articolo-prezzi__table-wrap">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Data import</th>
                      <th>Etichetta</th>
                      <th>Fornitore</th>
                      <th className="bop-text-right">Prezzo std. ({simboloAcquisto})</th>
                      <th className="bop-text-right">Var. %</th>
                      <th className="bop-text-right">Convert.</th>
                      <th className="bop-text-right">Prezzo vendita</th>
                      <th>Stato riga</th>
                      <th>Strategia</th>
                      <th>Importato da</th>
                    </tr>
                  </thead>
                  <tbody>
                    {righeVisibili.map(r => {
                      const prezzo   = toNum(r.prezzoStandard);
                      const conv     = toNum(r.convertitore) ?? convertitoreArticolo;
                      const prezzoVen = prezzo != null ? prezzo * conv : null;
                      const delta    = deltaPerRiga.get(r.id) ?? null;
                      const applicata = isApplicata(r);
                      return (
                        <tr
                          key={r.id}
                          className={applicata ? '' : 'articolo-prezzi__riga--non-applicata'}
                        >
                          <td>{formatDataOra(r.importListino?.importatoAt)}</td>
                          <td>
                            {r.importListino ? (
                              <Link
                                to={`/anagrafiche/articoli/listini/${r.importListino.id}`}
                                className="articolo-prezzi__link"
                              >
                                {r.importListino.etichetta}
                              </Link>
                            ) : '—'}
                          </td>
                          <td>{nomeFornitore(r.importListino?.fornitore)}</td>
                          <td className="bop-text-right articolo-prezzi__num">
                            € {formatCurrency(prezzo)}
                          </td>
                          <td className="bop-text-right articolo-prezzi__num">
                            {delta == null ? (
                              <span className="bop-text-muted">—</span>
                            ) : (
                              <span className={delta > 0
                                ? 'articolo-prezzi__delta--su'
                                : delta < 0 ? 'articolo-prezzi__delta--giu' : ''}
                              >
                                {delta > 0 ? '+' : ''}{formatNumber(delta)}%
                              </span>
                            )}
                          </td>
                          <td className="bop-text-right articolo-prezzi__num">
                            {formatQuantity(conv)}
                          </td>
                          <td className="bop-text-right articolo-prezzi__num">
                            € {formatCurrency(prezzoVen)}
                          </td>
                          <td>
                            <span className={`bop-badge ${STATO_RIGA_BADGE[r.stato]}`}>
                              {STATO_RIGA_LABEL[r.stato]}
                            </span>
                            {r.erroreDettaglio && (
                              <div className="articolo-prezzi__errore">{r.erroreDettaglio}</div>
                            )}
                          </td>
                          <td className="bop-text-muted">
                            {r.importListino?.strategiaAggiornamento === 'PREZZO_E_ANAGRAFICA'
                              ? 'Prezzo + anagrafica'
                              : 'Solo prezzo'}
                          </td>
                          <td className="bop-text-muted">{r.importListino?.utenteName || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="articolo-prezzi__nota bop-text-muted">
              Le righe di listino sono agganciate all'articolo tramite il codice{' '}
              <strong>{articolo.codice}</strong>. Un fornitore che usa un codice diverso
              per lo stesso articolo non compare in questo elenco.
            </p>
          </div>
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
};

export default StoricoPrezziArticoloPage;
